// Estilos compartilhados das telas do módulo Campanhas (/campaigns,
// /contact-lists, /contact-lists/:id/contacts, /files) — a anatomia do
// mockup "Campanhas A": header de página 60px, corpo rolável, card de tabela
// em CSS grid, pills de estado e icon-buttons de linha.
//
// O grid da tabela muda por tela, então vem por prop:
//   const c = useCampaignPageStyles({ grid: "1.6fr 1fr 90px" });
//
// Espelha pages/FlowBuilder/flowPageStyles.js. Classes específicas de uma
// tela (saldo SMS, pill de recorrência) ficam no makeStyles local dela.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const useCampaignPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const borderCard = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";

  return {
    // ---- header da página (60px, título + ações à direita) ----
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      // MainContainer já aplica 16px laterais; 12px aqui fecha os 28px do mockup.
      padding: "0 12px",
      borderBottom: `1px solid ${borderSoft}`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageTitleCount: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    headerActions: {
      marginLeft: "auto",
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
    },

    // ---- breadcrumb de página filha ("← Listas / Nome") ----
    breadcrumbBack: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontFamily: "inherit",
      color: theme.palette.text.secondary,
      background: "none",
      border: "none",
      padding: 0,
      cursor: "pointer",
      "&:hover": { color: theme.palette.text.primary },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    breadcrumbSep: {
      color: theme.palette.aeros.textTertiary,
    },

    // ---- controles do header ----
    searchPill: {
      width: 220,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: "5px 14px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
      "& input::placeholder": {
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
    outlineButton: {
      borderRadius: 10,
      padding: "7px 14px",
      fontSize: 12.5,
      fontWeight: 600,
      textTransform: "none",
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)"}`,
      color: theme.palette.text.primary,
      "&:hover": {
        borderColor: alpha(AEROS.cyan, 0.4),
        color: stateText(theme, AEROS.cyan, "info"),
        backgroundColor: alpha(AEROS.cyan, 0.06),
      },
      "&.Mui-disabled": {
        color: theme.palette.aeros.textTertiary,
        borderColor: theme.palette.divider,
      },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 700,
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },

    // ---- corpo da página ----
    pageBody: {
      flex: 1,
      padding: "16px 12px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },

    // ---- card da tabela em CSS grid ----
    tableCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    tableHeadRow: {
      display: "grid",
      gridTemplateColumns: ({ grid }) => grid,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
    },
    tableHeadCell: {
      padding: "12px 8px",
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: ({ grid }) => grid,
      alignItems: "center",
      fontSize: 13,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      "&:last-child": { borderBottom: "none" },
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    tableRowClickable: {
      cursor: "pointer",
    },
    tableCell: {
      padding: "11px 8px",
      minWidth: 0,
    },
    cellMuted: {
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    cellEllipsis: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cellActionsHead: {
      textAlign: "right",
      paddingRight: 20,
    },
    cellActions: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 6,
      paddingRight: 20,
    },

    // ---- célula "nome" com tile de ícone + sublinha ----
    nameCell: {
      padding: "11px 8px 11px 20px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
    },
    nameTile: {
      width: 32,
      height: 32,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      backgroundColor: alpha("#C08BFF", 0.12),
      color: stateText(theme, "#C08BFF", "purple"),
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    nameTileCyan: {
      backgroundColor: alpha(AEROS.cyan, 0.12),
      color: stateText(theme, AEROS.cyan, "info"),
    },
    nameText: {
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    nameSub: {
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
    },

    // ---- avatar com inicial ----
    initialAvatar: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      fontSize: 9,
      fontWeight: 800,
      color: "#07080D",
    },

    // ---- pills de estado ----
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },
    statusDot: {
      width: 6,
      height: 6,
      flexShrink: 0,
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
    toneSuccess: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.1),
      border: `1px solid ${alpha(AEROS.success, 0.3)}`,
    },
    toneWarning: {
      color: stateText(theme, AEROS.warning, "warning"),
      backgroundColor: alpha(AEROS.warning, 0.1),
      border: `1px solid ${alpha(AEROS.warning, 0.3)}`,
    },
    toneError: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.1),
      border: `1px solid ${alpha(AEROS.error, 0.3)}`,
    },
    toneInfo: {
      color: stateText(theme, AEROS.cyan, "info"),
      backgroundColor: alpha(AEROS.cyan, 0.1),
      border: `1px solid ${alpha(AEROS.cyan, 0.3)}`,
    },
    toneNeutral: {
      color: theme.palette.text.secondary,
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
    },

    // ---- icon-buttons de linha ----
    rowActionButton: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": {
        color: stateText(theme, AEROS.cyan, "info"),
        borderColor: alpha(AEROS.cyan, 0.4),
        backgroundColor: "transparent",
      },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    rowActionButtonDanger: {
      "&:hover": {
        color: stateText(theme, AEROS.error, "error"),
        borderColor: alpha(AEROS.error, 0.4),
        backgroundColor: "transparent",
      },
    },

    // ---- estados de carga / vazio ----
    loadingRow: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "28px 0",
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      padding: "48px 24px",
      textAlign: "center",
    },
    emptyIcon: {
      width: 48,
      height: 48,
      borderRadius: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 6,
      backgroundColor: alpha("#A044FF", 0.12),
      color: stateText(theme, "#C08BFF", "purple"),
      "& .MuiSvgIcon-root": { fontSize: 24 },
    },
    emptyTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    emptyText: {
      fontSize: 12,
      color: theme.palette.text.secondary,
    },

    // ---- faixa de aviso (linhas ignoradas/inválidas da importação) ----
    noticeCard: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "12px 16px",
      borderRadius: 12,
      fontSize: 12.5,
      lineHeight: 1.5,
      marginBottom: 14,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 18, flexShrink: 0 },
    },
    noticeInfo: {
      backgroundColor: alpha(AEROS.cyan, 0.06),
      border: `1px solid ${alpha(AEROS.cyan, 0.2)}`,
      "& > .MuiSvgIcon-root": { color: stateText(theme, AEROS.cyan, "info") },
    },
    noticeWarning: {
      backgroundColor: alpha(AEROS.warning, 0.06),
      border: `1px solid ${alpha(AEROS.warning, 0.25)}`,
      "& > .MuiSvgIcon-root": { color: stateText(theme, AEROS.warning, "warning") },
    },
    noticeTitle: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: 4,
    },
    noticeList: {
      maxHeight: 160,
      overflowY: "auto",
      ...theme.scrollbarStyles,
      fontSize: 11.5,
    },
    noticeClose: {
      marginLeft: "auto",
      padding: 2,
      color: theme.palette.aeros.textTertiary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
  };
});

export default useCampaignPageStyles;
